import type { Permission } from '@crm/contracts';
import type { ReactElement } from 'react';

import { paths } from '@/shared/config';

import { NavigationGroup, type NavigationItem } from './NavigationGroup';

interface OrganizationNavigationProps {
  organizationId: number;
  canManage: boolean;
  /** Sections the viewer may open: admins all, others as their position allows. */
  can: (permission: Permission) => boolean;
  onNavigate: () => void;
}

type Item = NavigationItem & { permission?: Permission; admin?: boolean };

function organizationGroups(id: number): Array<{ title: string; items: Item[] }> {
  return [
    {
      title: 'Компания',
      items: [
        { to: paths.organization(id), label: 'Обзор', icon: 'overview', end: true },
        {
          to: paths.objects(id),
          label: 'Работа и объекты',
          icon: 'construction',
          permission: 'objects.view',
        },
        {
          to: paths.documents(id),
          label: 'Документы',
          icon: 'document',
          permission: 'documents.view',
        },
        { to: paths.employees(id), label: 'Сотрудники', icon: 'users', admin: true },
      ],
    },
    {
      title: 'Техника',
      items: [
        {
          to: paths.equipment(id),
          label: 'Реестр техники',
          icon: 'equipment',
          permission: 'equipment.view',
        },
        // Everyone sees their own rentals; rentals.view shows everyone's.
        { to: paths.rentals(id), label: 'Заявки и расходы', icon: 'requests' },
        {
          to: paths.telematics(id),
          label: 'Мониторинг',
          icon: 'tracker',
          permission: 'telematics.view',
        },
      ],
    },
    {
      title: 'Склад',
      items: [
        {
          to: paths.warehouses(id),
          label: 'Склады',
          icon: 'warehouse',
          end: true,
          permission: 'warehouses.view',
        },
        { to: paths.catalog(id), label: 'Каталог', icon: 'catalog', permission: 'catalog.view' },
        {
          to: paths.stock(id),
          label: 'Остатки и операции',
          icon: 'stock',
          permission: 'stock.view',
        },
      ],
    },
  ];
}

export function OrganizationNavigation(props: OrganizationNavigationProps): ReactElement {
  const { organizationId, canManage, can, onNavigate } = props;
  const visible = (item: Item) =>
    (!item.admin || canManage) && (!item.permission || can(item.permission));
  return (
    <>
      {organizationGroups(organizationId).map((group) => {
        const items = group.items
          .filter(visible)
          .map(({ permission: _permission, admin: _admin, ...item }) => item);
        return items.length > 0 ? (
          <NavigationGroup
            key={group.title}
            title={group.title}
            items={items}
            onNavigate={onNavigate}
          />
        ) : null;
      })}
    </>
  );
}
